import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Skeleton } from '@material-ui/lab';
import { Grid, Box } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { BASE_URL } from '../../routes';
import Loader from '../../components/Loader';
import TabPanel from '../../components/TabPanel';
import InspectionData from './Tabs/InspectionData';
import BreadCrumb from '../../components/BreadCrumb';
import Title from '../../components/PageHeader/Title';
import Header from '../../components/PageHeader/Header';
import { getPersonSimpleName } from '../../utils/utils';
import InspectionComment from './Tabs/InspectionComment';
import PageNotFound from '../../components/PageNotFound';
import DetailPageComponent from '../../components/DetailPageComponent';
import { INSPECTION_DETAIL_TAB_LIST } from '../../store/app.constants';
import {
    Form,
    Inspection,
    Comment,
    BreadCrumbs,
    ViolationList,
    Application,
    Order,
} from '../../store/modules/application/types';
import Violation from '../LicenseDetailTabs/Tabs/Violation';
import InspectionDetail from './InspectionDetail';

type InspectionDetailTabsProps = {
    match: { params: { id: string } };
    getInspectionDetails: Function;
    isLoading: boolean;
    editForm: Form;
    inspectionDetail: Inspection;
    setActiveTab: Function;
    getInspectionCommentsList: Function;
    inspectionCommentList: Comment;
    getApplication: Function;
    getInspectionViolations: Function;
    inspectionViolations: ViolationList;
    hasMoreViolationAssociatedToInspection: boolean;
    addViolation: Function;
    deleteViolation: Function;
    application: Application;
    handleAddViolation: Function;
    hasMoreInspectionComments: boolean;
    inspectionViolationsLoading: boolean;
};
const InspectionDetailTabs: FunctionComponent<InspectionDetailTabsProps> = (props) => {
    const {
        getInspectionDetails,
        isLoading,
        editForm,
        inspectionDetail,
        setActiveTab,
        getInspectionCommentsList,
        getApplication,
        getInspectionViolations,
        inspectionViolations,
        hasMoreViolationAssociatedToInspection,
        addViolation,
        deleteViolation,
        application,
        hasMoreInspectionComments,
        inspectionViolationsLoading,
    } = props;

    const [tabList, setTabList] = useState<{ id: number; name: string }[]>();
    const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumbs>([]);
    const [doShowDetail, setDoShowDetail] = useState(true);
    const [isBreadCrumbsLoading, setIsBreadCrumbsLoading] = useState(true);

    const inspectionId = props.match.params.id;
    const licenseId = inspectionDetail.license?.id;

    const [skipViolation, setSkipViolation] = useState<number>(0);
    const [skipInspectionComment, setSkipInspectionComment] = useState<number>(0);

    const [inspectionViolationSortOrder, setInspectionViolationSortOrder] = useState<Order>('asc');
    const [inspectionViolationSortColumn, setInspectionViolationSortColumn] = useState<string>('dueDate asc');

    const violationLimit = 20;
    const inspectionCommentLimit = 20;

    const leftDetailBarStyle = classNames({
        'detail-container-closed': true,
        'detail-container-hidden': false,
    });

    const makeBreadCrumbs = useCallback(() => {
        if (inspectionDetail?.license && !!application?.person) {
            const licenseNameAndNumber =
                inspectionDetail.license.type.name +
                `${inspectionDetail.license?.licenseNumber ? ` ${inspectionDetail.license?.licenseNumber}` : ''}`;
            return [
                {
                    label: `${getPersonSimpleName(application?.person?.name)} > `,
                    path: BASE_URL + `/person/${application?.person?.id}/detail`,
                },
                {
                    label: licenseNameAndNumber,
                    path: BASE_URL + `/license/${inspectionDetail.license.id}/detail`,
                },
            ];
        }
        return [];
    }, [inspectionDetail, application]);

    useEffect(() => {
        getInspectionDetails(inspectionId);
    }, [getInspectionDetails, inspectionId]);

    useEffect(() => {
        setIsBreadCrumbsLoading(!(application?.id === licenseId));
    }, [application, setIsBreadCrumbsLoading, licenseId]);

    useEffect(() => {
        getInspectionViolations(inspectionId, {
            limit: violationLimit,
            skip: skipViolation,
            orderCol: inspectionViolationSortColumn,
            orderBy: inspectionViolationSortOrder,
        });
    }, [
        getInspectionViolations,
        inspectionId,
        skipViolation,
        inspectionViolationSortColumn,
        inspectionViolationSortOrder,
    ]);

    useEffect(() => {
        getInspectionCommentsList(inspectionId, { limit: inspectionCommentLimit, skip: skipInspectionComment });
    }, [inspectionId, getInspectionCommentsList, skipInspectionComment]);

    useEffect(() => {
        setTabList(INSPECTION_DETAIL_TAB_LIST);
    }, []);

    useEffect(() => {
        const response = makeBreadCrumbs();
        setBreadCrumbs(response);
    }, [makeBreadCrumbs]);

    useEffect(() => {
        if (!!licenseId) {
            getApplication(licenseId);
        }
    }, [licenseId, getApplication]);

    const onTabSelect = (tabName: string) => {
        setActiveTab(tabName);
    };

    const updateViolationSortBy = (sortBy: string) => {
        setInspectionViolationSortColumn(sortBy);
        setSkipViolation(0);
    };

    const updateViolationSortOrder = (sortOrder: Order) => {
        setInspectionViolationSortOrder(sortOrder);
        setSkipViolation(0);
    };

    const renderTabsPanel = (activeTab: number) => {
        const tab = tabList && tabList[activeTab];
        switch (tab?.id) {
            case 0:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <InspectionData inspectionDetails={inspectionDetail} editForm={editForm} />
                    </TabPanel>
                );
            case 1:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <Violation
                            inspectionId={inspectionId}
                            loading={inspectionViolationsLoading}
                            data={inspectionViolations}
                            skip={skipViolation}
                            setSkip={setSkipViolation}
                            hasMore={hasMoreViolationAssociatedToInspection}
                            addViolation={addViolation}
                            deleteViolation={deleteViolation}
                            application={application}
                            setSortColumn={updateViolationSortBy}
                            setSortOrder={updateViolationSortOrder}
                            sortBy={inspectionViolationSortColumn}
                            sortOrder={inspectionViolationSortOrder}
                        />
                    </TabPanel>
                );
            case 2:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <InspectionComment
                            inspectionDetails={inspectionDetail}
                            skip={skipInspectionComment}
                            setSkip={setSkipInspectionComment}
                            hasMore={hasMoreInspectionComments}
                            doShowDetail={doShowDetail}
                        />
                    </TabPanel>
                );

            default:
                return <h1>Something went wrong. We are on it.</h1>;
        }
    };
    const StatusHeader = () => {
        return (
            <>
                <Header>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                        {isBreadCrumbsLoading ? (
                            <Skeleton className="bread-crumbs" width="10%" style={{ marginLeft: '30px' }} />
                        ) : (
                            <BreadCrumb breadCrumbs={breadCrumbs} />
                        )}
                        <Title titleText={`${inspectionDetail?.type?.name}`} />
                    </Grid>

                    <>
                        {doShowDetail && (
                            <KeyboardArrowLeft
                                className="detail-show-hide-btn opened"
                                onClick={() => {
                                    setDoShowDetail(!doShowDetail);
                                }}
                            />
                        )}
                        {!doShowDetail && (
                            <KeyboardArrowRight
                                className="detail-show-hide-btn closed"
                                onClick={() => {
                                    setDoShowDetail(!doShowDetail);
                                }}
                            />
                        )}
                    </>
                </Header>
                <>
                    {doShowDetail && (
                        <Box
                            className="backdrop"
                            onClick={() => {
                                setDoShowDetail(!doShowDetail);
                            }}
                        ></Box>
                    )}
                </>
            </>
        );
    };

    if (isLoading) return <Loader />;
    else if (!isLoading && !inspectionDetail.id) return <PageNotFound />;

    return (
        <div className="verticalFillContainer">
            <StatusHeader />
            <Box width="100%" display="flex" className="detail detail-mobile verticalFillContent">
                <InspectionDetail
                    className={leftDetailBarStyle}
                    doShowDetail={doShowDetail}
                    inspection={inspectionDetail!}
                />
                <DetailPageComponent
                    displayTabs={renderTabsPanel}
                    tabList={tabList}
                    onTabSelect={onTabSelect}
                ></DetailPageComponent>
            </Box>
        </div>
    );
};

export default InspectionDetailTabs;
